import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/UI/Button';
import MultiSelect from '../../components/UI/MultiSelect';
import { useToast } from '../../components/UI/ToastProvider';
import {
  EXTERNAL_CHANNEL_TYPE_LABELS,
  ExternalChannelType,
  ExternalChannelTypeIcon,
  ExternalChannelTypeSelect,
} from './ExternalChannelTypeSelect';
import { agentBotService } from '../../services/agentBotService';
import {
  ClassificationNotifyRules,
  supportNotificationService,
  SupportExternalChannel,
  SupportNotificationEvent,
} from '../../services/supportNotificationService';
import { ClassificationSchema } from '../../types/classificationSchema';
import { KEEP_EXISTING_SECRET_HINT } from '../../types/inboxConfiguration';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

interface SupportExternalChannelsSectionProps {
  inboxId: string;
  agentBotId?: string | null;
}

const CLASSIFICATION_KEY_SEPARATOR = '::';
const emptyClassificationRules = (): ClassificationNotifyRules => ({ anyOf: [] });

const toClassificationKey = (fieldCode: string, slug: string) =>
  `${fieldCode}${CLASSIFICATION_KEY_SEPARATOR}${slug}`;

const parseClassificationKey = (key: string): { fieldCode: string; slug: string } | null => {
  const separatorIndex = key.indexOf(CLASSIFICATION_KEY_SEPARATOR);
  if (separatorIndex <= 0)
  {
    return null;
  }

  const fieldCode = key.slice(0, separatorIndex).trim();
  const slug = key.slice(separatorIndex + CLASSIFICATION_KEY_SEPARATOR.length).trim();
  if (!fieldCode || !slug)
  {
    return null;
  }

  return { fieldCode, slug };
};

const selectedIdsToRules = (ids: string[]): ClassificationNotifyRules => {
  const slugsByField = new Map<string, string[]>();
  for (const id of ids)
  {
    const parsed = parseClassificationKey(id);
    if (!parsed)
    {
      continue;
    }

    const current = slugsByField.get(parsed.fieldCode) ?? [];
    if (!current.includes(parsed.slug))
    {
      current.push(parsed.slug);
    }
    slugsByField.set(parsed.fieldCode, current);
  }

  return {
    anyOf: Array.from(slugsByField.entries()).map(([fieldCode, slugs]) => ({
      fieldCode,
      slugs,
    })),
  };
};

const rulesToSelectedIds = (rules?: ClassificationNotifyRules | null): string[] => {
  if (!rules?.anyOf)
  {
    return [];
  }

  const ids: string[] = [];
  for (const rule of rules.anyOf)
  {
    for (const slug of rule.slugs ?? [])
    {
      if (!slug)
      {
        continue;
      }

      ids.push(toClassificationKey(rule.fieldCode, slug));
    }
  }

  return ids;
};

type SupportedExternalChannel = Extract<ExternalChannelType, 'Discord' | 'Email'>;

type ChannelDraft = {
  id?: string;
  name: string;
  channel: SupportedExternalChannel;
  enabled: boolean;
  webhookUrl: string;
  webhookUrlMasked?: string | null;
  recipients: string[];
  eventTypes: string[];
  classificationRules: ClassificationNotifyRules;
  isNew: boolean;
};

const defaultNameForChannel = (channel: SupportedExternalChannel): string =>
  channel === 'Discord' ? 'Discord' : 'E-mail de equipe';

const toDraft = (channel: SupportExternalChannel): ChannelDraft => ({
  id: channel.id,
  name: channel.name,
  channel: channel.channel === 'Email' ? 'Email' : 'Discord',
  enabled: channel.enabled,
  webhookUrl: '',
  webhookUrlMasked: channel.webhookUrlMasked,
  recipients: channel.recipients ?? [],
  eventTypes: channel.eventTypes ?? [],
  classificationRules: channel.classificationRules ?? emptyClassificationRules(),
  isNew: false,
});

const createEmptyDraft = (): ChannelDraft => ({
  name: defaultNameForChannel('Discord'),
  channel: 'Discord',
  enabled: false,
  webhookUrl: '',
  recipients: [],
  eventTypes: [],
  classificationRules: emptyClassificationRules(),
  isNew: true,
});

const destinationSummary = (draft: ChannelDraft): string => {
  if (draft.channel === 'Email')
  {
    if (draft.recipients.length === 0)
    {
      return 'Sem destinatários';
    }

    if (draft.recipients.length === 1)
    {
      return draft.recipients[0];
    }

    return `${draft.recipients.length} destinatários`;
  }

  if (draft.webhookUrlMasked || draft.webhookUrl.trim())
  {
    return 'Webhook configurado';
  }

  return 'Webhook pendente';
};


export const SupportExternalChannelsSection: React.FC<SupportExternalChannelsSectionProps> = ({
  inboxId,
  agentBotId,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<SupportNotificationEvent[]>([]);
  const [schema, setSchema] = useState<ClassificationSchema | null>(null);
  const [drafts, setDrafts] = useState<ChannelDraft[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [newRecipientByKey, setNewRecipientByKey] = useState<Record<string, string>>({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const draftKey = (draft: ChannelDraft, index: number) => draft.id ?? `new-${index}`;

  const eventOptions = useMemo(
    () =>
      events.map((event) => ({
        id: event.code,
        name: event.title,
        description: event.isEmitted
          ? event.description
          : `${event.description} (ainda não disparado — pode pré-configurar)`,
      })),
    [events]
  );

  const classificationOptions = useMemo(() => {
    const options: { id: string; name: string; description?: string }[] = [];
    const seen = new Set<string>();
    const fields = schema?.fields ?? [];

    for (const field of fields)
    {
      if (!field.isActive)
      {
        continue;
      }

      for (const option of field.options ?? [])
      {
        if (!option.isActive)
        {
          continue;
        }

        const id = toClassificationKey(field.code, option.slug);
        seen.add(id);
        options.push({
          id,
          name: `${field.title} — ${option.name}`,
          description: option.criteria ?? undefined,
        });
      }
    }

    for (const draft of drafts)
    {
      for (const rule of draft.classificationRules.anyOf)
      {
        const field = fields.find((item) => item.code === rule.fieldCode);
        for (const slug of rule.slugs)
        {
          const id = toClassificationKey(rule.fieldCode, slug);
          if (seen.has(id))
          {
            continue;
          }

          seen.add(id);
          const optionName = field?.options.find((item) => item.slug === slug)?.name ?? slug;
          const fieldTitle = field?.title ?? rule.fieldCode;
          options.push({
            id,
            name: `${fieldTitle} — ${optionName}`,
            description: 'Opção não está mais no schema do robô',
          });
        }
      }
    }

    return options;
  }, [drafts, schema]);

  const load = useCallback(async () => {
    setLoading(true);
    try
    {
      const [eventsData, channelsData] = await Promise.all([
        supportNotificationService.getNotificationEvents(),
        supportNotificationService.getExternalChannels(inboxId),
      ]);

      let schemaData: ClassificationSchema | null = null;
      if (agentBotId)
      {
        try
        {
          schemaData = await agentBotService.getClassificationSchema(agentBotId);
        }
        catch
        {
          addToast('Não foi possível carregar as classificações do robô', 'warning');
        }
      }

      setEvents(eventsData);
      setSchema(schemaData);
      const nextDrafts = channelsData.map(toDraft);
      setDrafts(nextDrafts);
      setExpandedKey(nextDrafts.length === 1 && nextDrafts[0].id ? nextDrafts[0].id : null);
    }
    catch
    {
      addToast('Erro ao carregar canais externos', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast, agentBotId, inboxId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateDraft = (index: number, patch: Partial<ChannelDraft>) => {
    setDrafts((current) =>
      current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft))
    );
  };

  const handleChannelTypeChange = (index: number, nextType: ExternalChannelType) => {
    if (nextType === 'WhatsApp')
    {
      return;
    }

    const draft = drafts[index];
    if (!draft.isNew)
    {
      return;
    }

    const previousDefaultName = defaultNameForChannel(draft.channel);
    const shouldReplaceName =
      !draft.name.trim() || draft.name.trim() === previousDefaultName;

    updateDraft(index, {
      channel: nextType,
      name: shouldReplaceName ? defaultNameForChannel(nextType) : draft.name,
      webhookUrl: nextType === 'Discord' ? draft.webhookUrl : '',
      recipients: nextType === 'Email' ? draft.recipients : [],
    });
  };

  const handleEventsChange = async (channelId: string, eventCodes: string[]) => {
    const index = drafts.findIndex((draft) => draft.id === channelId);
    if (index < 0)
    {
      return;
    }

    const draft = drafts[index];
    const previous = draft.eventTypes;
    updateDraft(index, { eventTypes: eventCodes });
    
    try
    {
      const updated = await supportNotificationService.updateExternalChannel(channelId, {
        name: draft.name.trim(),
        enabled: draft.enabled,
        webhookUrl: draft.channel === 'Discord' ? draft.webhookUrl.trim() || undefined : undefined,
        recipients: draft.channel === 'Email' ? draft.recipients : undefined,
        eventTypes: eventCodes,
      });
      setDrafts((current) =>
        current.map((item, i) => (i === index ? toDraft(updated) : item))
      );
      addToast('Eventos salvos', 'success');
    }
    catch
    {
      updateDraft(index, { eventTypes: previous });
      addToast('Erro ao salvar eventos do canal', 'error');
    }
  };

  const handleClassificationsChange = async (channelId: string, selectedIds: string[]) => {
    const index = drafts.findIndex((draft) => draft.id === channelId);
    if (index < 0)
    {
      return;
    }

    const draft = drafts[index];
    const previous = draft.classificationRules;
    const nextRules = selectedIdsToRules(selectedIds);
    updateDraft(index, { classificationRules: nextRules });
    
    try
    {
      const updated = await supportNotificationService.updateExternalChannel(channelId, {
        name: draft.name.trim(),
        enabled: draft.enabled,
        webhookUrl: draft.channel === 'Discord' ? draft.webhookUrl.trim() || undefined : undefined,
        recipients: draft.channel === 'Email' ? draft.recipients : undefined,
        classificationRules: nextRules,
      });
      setDrafts((current) =>
        current.map((item, i) => (i === index ? toDraft(updated) : item))
      );
      addToast('Classificações salvas', 'success');
    }
    catch
    {
      updateDraft(index, { classificationRules: previous });
      addToast('Erro ao salvar classificações do canal', 'error');
    }
  };

  const handleAddRecipient = (index: number) => {
    const key = draftKey(drafts[index], index);
    const email = (newRecipientByKey[key] ?? '').trim();
    if (!email)
    {
      return;
    }

    const draft = drafts[index];
    if (draft.recipients.some((r) => r.toLowerCase() === email.toLowerCase()))
    {
      addToast('Este e-mail já está na lista', 'warning');
      return;
    }

    updateDraft(index, { recipients: [...draft.recipients, email] });
    setNewRecipientByKey((current) => ({ ...current, [key]: '' }));
  };

  const handleSaveChannel = async (index: number) => {
    const draft = drafts[index];
    const key = draftKey(draft, index);

    if (!draft.name.trim())
    {
      addToast('Informe o nome do canal', 'error');
      return;
    }

    if (draft.channel === 'Discord' && draft.enabled)
    {
      const hasWebhookChange = draft.webhookUrl.trim().length > 0;
      if (!hasWebhookChange && !draft.webhookUrlMasked)
      {
        addToast('Informe a URL do webhook do Discord', 'error');
        return;
      }
    }

    if (draft.channel === 'Email' && draft.enabled && draft.recipients.length === 0)
    {
      addToast('Adicione ao menos um destinatário', 'error');
      return;
    }

    setSavingId(key);
    try
    {
      if (draft.isNew)
      {
        const created = await supportNotificationService.createExternalChannel({
          inboxId,
          name: draft.name.trim(),
          channel: draft.channel,
          enabled: draft.enabled,
          webhookUrl: draft.channel === 'Discord' ? draft.webhookUrl.trim() : undefined,
          recipients: draft.channel === 'Email' ? draft.recipients : undefined,
          eventTypes: draft.eventTypes,
          classificationRules: draft.classificationRules,
        });
        setDrafts((current) =>
          current.map((item, i) => (i === index ? toDraft(created) : item))
        );
        setExpandedKey(created.id);
        addToast('Canal criado', 'success');
      }
      else if (draft.id)
      {
        const updated = await supportNotificationService.updateExternalChannel(draft.id, {
          name: draft.name.trim(),
          enabled: draft.enabled,
          webhookUrl: draft.channel === 'Discord' ? draft.webhookUrl.trim() : undefined,
          recipients: draft.channel === 'Email' ? draft.recipients : undefined,
          eventTypes: draft.eventTypes,
          classificationRules: draft.classificationRules,
        });
        setDrafts((current) =>
          current.map((item, i) => (i === index ? toDraft(updated) : item))
        );
        addToast('Canal salvo', 'success');
      }
    }
    catch
    {
      addToast('Erro ao salvar canal', 'error');
    }
    finally
    {
      setSavingId(null);
    }
  };

  const handleTest = async (index: number) => {
    const draft = drafts[index];
    if (!draft.id)
    {
      addToast('Salve o canal antes de enviar o teste', 'warning');
      return;
    }

    setTestingId(draft.id);
    try
    {
      await supportNotificationService.testExternalChannel(draft.id);
      addToast('Mensagem de teste enviada', 'success');
    }
    catch
    {
      addToast('Falha ao enviar teste. Verifique a configuração do canal.', 'error');
    }
    finally
    {
      setTestingId(null);
    }
  };

  const handleDelete = async (index: number) => {
    const draft = drafts[index];
    if (draft.isNew || !draft.id)
    {
      const key = draftKey(draft, index);
      setDrafts((current) => current.filter((_, i) => i !== index));
      setExpandedKey((current) => (current === key ? null : current));
      return;
    }

    if (
      !window.confirm(
        `Remover o canal "${draft.name}"? Os vínculos de eventos também serão excluídos.`
      )
    )
    {
      return;
    }

    const channelId = draft.id;
    setDeletingId(channelId);
    try
    {
      await supportNotificationService.deleteExternalChannel(channelId);
      setDrafts((current) => current.filter((_, i) => i !== index));
      setExpandedKey((current) => (current === channelId ? null : current));
      addToast('Canal removido', 'success');
    }
    catch
    {
      addToast('Erro ao remover canal', 'error');
    }
    finally
    {
      setDeletingId(null);
    }
  };

  if (loading)
  {
    return (
      <section className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="h-5 w-48 rounded bg-gray-200" />
              <div className="h-4 w-72 rounded bg-gray-100" />
            </div>
            <div className="h-9 w-32 rounded-md bg-gray-200" />
          </div>
          <div className="rounded-lg border border-gray-200 divide-y divide-gray-200">
            <div className="h-16 bg-gray-50" />
            <div className="h-16 bg-gray-50" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Notificações externas</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Cadastre canais externos e escolha, em cada um, quais eventos operacionais e quais
            classificações do robô devem disparar notificações para a equipe.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setExpandedKey(`new-${drafts.length}`);
            setDrafts((current) => [...current, createEmptyDraft()]);
          }}
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          Adicionar canal
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
          <p className="text-sm font-medium text-gray-900">Nenhum canal configurado</p>
          <p className="text-sm text-gray-500 mt-1">
            Adicione Discord ou e-mail para avisar a equipe sobre eventos do atendimento.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-4"
            onClick={() => {
              setExpandedKey('new-0');
              setDrafts([createEmptyDraft()]);
            }}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            Criar primeiro canal
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-200">
          {drafts.map((draft, index) => {
            const key = draftKey(draft, index);
            const isExpanded = expandedKey === key;
            const isSaving = savingId === key;
            const isTesting = testingId === draft.id;
            const isDeleting = deletingId === draft.id;
            const selectedEventCodes = draft.eventTypes;
            const selectedEventTitles = selectedEventCodes.map(
              (code) => events.find((event) => event.code === code)?.title ?? code
            );
            const selectedClassificationIds = rulesToSelectedIds(draft.classificationRules);
            const selectedClassificationTitles = selectedClassificationIds.map(
              (id) => classificationOptions.find((option) => option.id === id)?.name ?? id
            );
            const collapsedChipTitles = [...selectedEventTitles, ...selectedClassificationTitles];
            const visibleChipTitles = collapsedChipTitles.slice(0, 3);
            const extraChipCount = collapsedChipTitles.length - visibleChipTitles.length;

            return (
              <div
                key={key}
                className={isExpanded ? 'bg-blue-50/40' : 'bg-white hover:bg-gray-50/80'}
              >
                <div className="flex items-start gap-2 px-3 py-3 sm:px-4">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-3 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={() => setExpandedKey(isExpanded ? null : key)}
                    aria-expanded={isExpanded}
                  >
                    <ChevronRightIcon
                      className={`mt-2.5 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                        isExpanded ? 'rotate-90 text-blue-600' : ''
                      }`}
                    />
                    <div
                      className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                        draft.channel === 'Discord'
                          ? 'bg-[#5865F2]/10'
                          : 'bg-amber-50'
                      }`}
                    >
                      <ExternalChannelTypeIcon
                        channelType={draft.channel}
                        className="h-5 w-5"
                      />
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {draft.name || 'Novo canal'}
                        </h3>
                        <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-600">
                          {EXTERNAL_CHANNEL_TYPE_LABELS[draft.channel]}
                        </span>
                        {draft.isNew ? (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200/80">
                            Rascunho
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                              draft.enabled
                                ? 'bg-green-50 text-green-700 ring-1 ring-green-200/80'
                                : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200/80'
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                draft.enabled ? 'bg-green-500' : 'bg-gray-400'
                              }`}
                            />
                            {draft.enabled ? 'Ativo' : 'Inativo'}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 truncate">
                        {destinationSummary(draft)}
                        {selectedEventTitles.length > 0 && (
                          <span className="text-gray-400">
                            {' '}
                            · {selectedEventTitles.length}{' '}
                            {selectedEventTitles.length === 1 ? 'evento' : 'eventos'}
                          </span>
                        )}
                        {selectedClassificationTitles.length > 0 && (
                          <span className="text-gray-400">
                            {' '}
                            · {selectedClassificationTitles.length}{' '}
                            {selectedClassificationTitles.length === 1
                              ? 'classificação'
                              : 'classificações'}
                          </span>
                        )}
                      </p>
                      {!isExpanded && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {collapsedChipTitles.length === 0 ? (
                            <span className="text-xs text-gray-400 italic">
                              Nenhum evento ou classificação vinculada
                            </span>
                          ) : (
                            <>
                              {visibleChipTitles.map((title, chipIndex) => (
                                <span
                                  key={`${title}-${chipIndex}`}
                                  className="rounded-md bg-white px-2 py-0.5 text-xs text-gray-700 ring-1 ring-gray-200"
                                >
                                  {title}
                                </span>
                              ))}
                              {extraChipCount > 0 && (
                                <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                  +{extraChipCount}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-red-600"
                    title="Remover canal"
                    aria-label={`Remover canal ${draft.name || 'Novo canal'}`}
                    onClick={() => void handleDelete(index)}
                    disabled={isDeleting}
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>

                {isExpanded && (
                  <div className="space-y-4 border-t border-gray-200 bg-gray-50/80 px-4 pb-4 pt-4 sm:px-5">
                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de canal</label>
                        <ExternalChannelTypeSelect
                          value={draft.channel}
                          onChange={(nextType) => handleChannelTypeChange(index, nextType)}
                          disabled={!draft.isNew}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-900 focus:border-blue-900"
                          value={draft.name}
                          onChange={(e) => updateDraft(index, { name: e.target.value })}
                          placeholder="Ex.: Operação, Comercial"
                        />
                      </div>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900"
                        checked={draft.enabled}
                        onChange={(e) => updateDraft(index, { enabled: e.target.checked })}
                      />
                      <span>
                        <span className="block font-medium text-gray-900">Ativo</span>
                        <span className="block text-sm text-gray-500">
                          Só dispara quando estiver ligado e tiver ao menos um evento ou
                          classificação vinculada.
                        </span>
                      </span>
                    </label>

                    {draft.channel === 'Discord' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          URL do webhook
                        </label>
                        <input
                          type="url"
                          autoComplete="off"
                          spellCheck={false}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-900 focus:border-blue-900"
                          placeholder={
                            draft.webhookUrlMasked
                              ? draft.webhookUrlMasked
                              : 'https://discord.com/api/webhooks/...'
                          }
                          value={draft.webhookUrl}
                          onChange={(e) => updateDraft(index, { webhookUrl: e.target.value })}
                        />
                        {Boolean(draft.webhookUrlMasked) && (
                          <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Destinatários
                        </label>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="email"
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-900 focus:border-blue-900"
                            placeholder="equipe@empresa.com"
                            value={newRecipientByKey[key] ?? ''}
                            onChange={(e) =>
                              setNewRecipientByKey((current) => ({
                                ...current,
                                [key]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter')
                              {
                                e.preventDefault();
                                handleAddRecipient(index);
                              }
                            }}
                          />
                          <Button variant="secondary" onClick={() => handleAddRecipient(index)}>
                            Adicionar
                          </Button>
                        </div>
                        {draft.recipients.length === 0 ? (
                          <p className="text-xs text-gray-500">Nenhum destinatário adicionado.</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {draft.recipients.map((email) => (
                              <li
                                key={email}
                                className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                              >
                                <span className="text-gray-800 truncate">{email}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex-shrink-0 text-gray-400 hover:text-red-600"
                                  title="Remover destinatário"
                                  aria-label={`Remover destinatário ${email}`}
                                  onClick={() =>
                                    updateDraft(index, {
                                      recipients: draft.recipients.filter((r) => r !== email),
                                    })
                                  }
                                >
                                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <div className="space-y-4">
                      <MultiSelect
                        label="Eventos que disparam este canal"
                        hint="Eventos operacionais do atendimento, como transferência do bot ou estouro de fila."
                        options={eventOptions}
                        selectedIds={draft.eventTypes}
                        onChange={(selectedIds) => {
                          if (draft.id && !draft.isNew)
                          {
                            void handleEventsChange(draft.id, selectedIds);
                            return;
                          }

                          updateDraft(index, { eventTypes: selectedIds });
                        }}
                        placeholder="Buscar evento..."
                        emptyMessage="Nenhum evento encontrado"
                        showBulkActions
                      />

                      <div>
                        <MultiSelect
                          label="Classificações que disparam este canal"
                          hint="Opções do schema de classificação do robô, avaliadas no encerramento da conversa."
                          options={classificationOptions}
                          selectedIds={rulesToSelectedIds(draft.classificationRules)}
                          onChange={(selectedIds) => {
                            if (draft.id && !draft.isNew)
                            {
                              void handleClassificationsChange(draft.id, selectedIds);
                              return;
                            }

                            updateDraft(index, {
                              classificationRules: selectedIdsToRules(selectedIds),
                            });
                          }}
                          placeholder="Buscar classificação..."
                          emptyMessage="Nenhuma classificação encontrada"
                          showBulkActions
                        />
                        {!agentBotId && (
                          <p className="text-xs text-gray-500 mt-1">
                            Vincule um robô na aba Geral para escolher as classificações que
                            disparam este canal.{' '}
                            <Link
                              to={helpdeskRoutes.settingsBots}
                              className="font-medium text-blue-700 underline"
                            >
                              Gerenciar robôs
                            </Link>
                          </p>
                        )}
                      </div>
                    </div>
                    </div>

                    <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleSaveChannel(index)}
                        disabled={isSaving}
                      >
                        {isSaving ? 'Salvando...' : 'Salvar canal'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void handleTest(index)}
                        disabled={isTesting || draft.isNew}
                      >
                        {isTesting ? 'Enviando...' : 'Enviar teste'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
